import React from 'react';

import { Box, Grid } from '@mui/material';

import { DateFormatPatterns, STATUSES_OPTIONS } from '@clearverify/constants';
import { formatDate, formatPhoneNumber } from '@clearverify/utils';

import {
  UserStatus,
  useUpdateUserRoleMutation,
  VerifierStaffListDocument,
  VerifierStaffRole,
} from 'src/api';
import { Button, FullscreenLoader } from 'src/components/common';
import { Field, Form, SelectField } from 'src/components/finalForm';
import { VERIFIER_ROLE_OPTIONS } from 'src/constants/verifier';
import { useNotification } from 'src/hooks/app';
import { useVerifierStaff } from 'src/hooks/verifierStaff';
import { isEnableStatus } from 'src/utils/user';

import { DetailGridItem } from '../../common';

import { FormFields, FormValues } from './types';

export interface VerifierStaffViewDetailsByClearVerifyStaffModalFormProps {
  onCloseModal: () => void;
  verifierStaffId: string | undefined;
  switchClosability: (closability: boolean) => void;
  isAdmin: boolean;
}

export const VerifierStaffViewDetailsByClearVerifyStaffModalForm: React.FC<
  VerifierStaffViewDetailsByClearVerifyStaffModalFormProps
> = ({ switchClosability, onCloseModal, verifierStaffId, isAdmin }) => {
  const { createApolloContext } = useNotification();

  const { verifierStaff, loading } = useVerifierStaff(verifierStaffId);

  const [updateUserRole] = useUpdateUserRoleMutation({
    refetchQueries: [VerifierStaffListDocument],
    context: createApolloContext({
      successMessage: 'Verifier Staff has been successfully updated',
    }),
  });

  const handleSubmit = React.useCallback(
    async (formValues: FormValues) => {
      try {
        const userId = verifierStaff?.user.id || '';
        const currentStatus = verifierStaff?.user.status;
        const newStatus = formValues.userStatus;

        const hasStatusChanged = currentStatus && currentStatus !== newStatus;

        await updateUserRole({
          variables: {
            userId,
            data: {
              verifierStaff: {
                update: {
                  role: formValues.businessRole,
                },
              },
              ...(hasStatusChanged ? { status: newStatus } : null),
            },
          },
        });

        switchClosability(true);
        onCloseModal();
      } catch {
        switchClosability(true);
      }
    },
    [
      onCloseModal,
      switchClosability,
      updateUserRole,
      verifierStaff?.user.id,
      verifierStaff?.user.status,
    ],
  );

  const { firstName, lastName, birthDate, phoneNumber, email, status } = verifierStaff?.user ?? {};

  const initialValues: FormValues = React.useMemo(
    () => ({
      [FormFields.userStatus]: verifierStaff?.user.status || UserStatus.Inactive,
      [FormFields.businessRole]: verifierStaff?.role || VerifierStaffRole.VerifierManager,
    }),
    [verifierStaff?.role, verifierStaff?.user.status],
  );

  const shouldRenderStatusField = isEnableStatus(status);

  if (loading) {
    return <FullscreenLoader />;
  }

  return (
    <Form initialValues={initialValues} onSubmit={handleSubmit}>
      {({ dirty, submitting }) => (
        <React.Fragment>
          <Grid container spacing={1.5}>
            <DetailGridItem topText="First Name" bottomText={firstName} />
            <DetailGridItem topText="Last Name" bottomText={lastName} />
            <DetailGridItem topText="Email Address" bottomText={email} />
            <DetailGridItem
              topText="Date of Birth"
              bottomText={formatDate(birthDate, DateFormatPatterns.monthDayYearDots)}
            />
            <DetailGridItem topText="Phone Number" bottomText={formatPhoneNumber(phoneNumber)} />
          </Grid>

          {isAdmin && (
            <Grid container my={1} spacing={2} size={12}>
              {shouldRenderStatusField && (
                <Grid size={12}>
                  <Field
                    name={FormFields.userStatus}
                    label="Status"
                    component={SelectField}
                    fullWidth
                    options={STATUSES_OPTIONS}
                    disabled={submitting}
                  />
                </Grid>
              )}

              <Grid size={12}>
                <Field
                  name={FormFields.businessRole}
                  component={SelectField}
                  label="Role"
                  options={VERIFIER_ROLE_OPTIONS}
                  fullWidth
                />
              </Grid>
            </Grid>
          )}

          <Box display="flex" gap={2} mt={4} justifyContent="center">
            <Button variant="outlined" color="error" onClick={onCloseModal} disabled={submitting}>
              Close
            </Button>

            {isAdmin && (
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || !dirty}
                loading={submitting}
              >
                Save Changes
              </Button>
            )}
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};
